// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage from "@bg/MobiusOverrides/LazyImage";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { styledScrollbar } from "@bg/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getProductIdsForCompare,
    getProductsForCompare,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import loadProductIdsToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/LoadProductIdsToCompare";
import loadProductsToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/LoadProductsToCompare";
import removeAllProductsFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveAllProductsFromCompare";
import removeProductIdToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import setProductCompareReturnUrl from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/SetProductCompareReturnUrl";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { CarouselSlide, CarouselStyles } from "@insite/content-library/Components/Carousel";
import ProductCarouselProductCondensed from "@insite/content-library/Components/ProductCarouselProductCondensed";
import Button, { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps, DrawerProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

import { css } from "styled-components";

const PRODUCT_COMPARE_CAROUSEL_ID = "product-compare-carousel-id";

interface OwnProps {
    extendedStyles?: ProductCompareFlyOutStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapStateToProps = (state: ApplicationState) => ({
    productIds: getProductIdsForCompare(state),
    products: getProductsForCompare(state),
    productComparePageLink: getPageLinkByPageType(state, "ProductComparePage"),
    currentLocation: getLocation(state),
});

const mapDispatchToProps = {
    loadProductIdsToCompare,
    loadProductsToCompare,
    removeProductIdToCompare,
    removeAllProductsFromCompare,
    setProductCompareReturnUrl,
};

export interface ProductCompareFlyOutStyles {
    carousel?: CarouselStyles;
    carouselDrawer?: DrawerPresentationProps;
    carouselDrawerContainer?: InjectableCss;
    removeProductFromCompareButton?: ClickablePresentationProps;
    removeProductFromCompareButtonIcon?: ButtonIconPresentationProps;
    headerGridContainer?: GridContainerProps;
    headerTitleGridItem?: GridItemProps;
    headerTitleText?: TypographyPresentationProps;
    headerActionsGridItem?: GridItemProps;
    headerRemoveAllButton?: ClickablePresentationProps;
    removeAllText?: TypographyPresentationProps;
    headerCompareButton?: ButtonPresentationProps;

    flyOutContent?: InjectableCss;
    flyOutContentItem?: InjectableCss;
    clearIcon?: IconProps;
}

export const productCompareFlyOutStyles: ProductCompareFlyOutStyles = {
    carouselDrawer: {
        position: "bottom",
        size: 145,
        cssOverrides: {
            drawerContainer: css`
                > div {
                    transform: translate(0, 0);
                    @media (max-width: 1300px) {
                        height: 250px;
                    }
                    @media (max-width: 768px) {
                        height: 135px;
                    }
                }
            `,
            scrim: css`
                @media (max-width: 1300px) {
                    height: 250px;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                height: 135px;
                            `,
                            css`
                                height: 135px;
                            `,
                        ],
                        "max",
                    )}
            `,
            drawerBody: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                height: 125px;
                            `,
                        ],
                        "max",
                    )}
            `,
            drawerTitle: css`
                display: none;
            `,
            drawerContent: css`
                ${styledScrollbar()}
                @media (min-width: 1353px) {
                    overflow: hidden;
                }
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    carouselDrawerContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            @media (max-width: 1300px) {
                flex-wrap: wrap;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )};
        `,
    },
    carousel: {
        titleText: {
            css: css`
                display: none;
            `,
        },
    },
    removeProductFromCompareButton: {
        css: css`
            position: absolute;
            right: -8px;
            top: -8px;
            width: 18px;
            cursor: pointer;
        `,
    },
    headerGridContainer: {
        gap: 0,
        css: css`
            margin: 0 auto;
        `,
    },
    headerTitleGridItem: {
        width: [12, 6, 6, 6, 6],
        align: "middle",
    },
    headerTitleText: {
        color: "text",
        css: css`
            font-size: ${fontConfig.fontSize.large}px;
            letter-spacing: 0;
            line-height: ${fontConfig.lineHeight.singleLineSmall};
            text-align: right;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 10px;
                        `,
                        css`
                            margin-bottom: 10px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    headerActionsGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            justify-content: flex-end;
        `,
    },
    headerRemoveAllButton: {
        css: css`
            margin-left: 16px;
        `,
    },
    removeAllText: {
        color: "secondary",
        lineHeight: fontConfig.lineHeight.singleLineSmall,
        weight: fontConfig.weight.semiBold,
        css: css`
            letter-spacing: 0.2px;
            opacity: 0.8;
        `,
    },
    headerCompareButton: {
        css: css`
            margin: 5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex: 1;
                        `,
                    ],
                    "max",
                )}
        `,
    },

    flyOutContent: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 22px 30px 30px;
            flex-wrap: wrap;
            @media (max-width: 1300px) {
                flex-direction: column;
                justify-content: center;
                align-items: center;
                padding: 16px 30px 8px;
            }
        `,
    },
    flyOutContentItem: {
        css: css`
            position: relative;
            margin: 0px 15px;

            img {
                max-height: 85px !important;
            }
            @media (max-width: 1300px) {
                margin: 30px;
            }
            &:first-child {
                margin-left: 30px;
            }
            &:last-child {
                margin-right: 30px;
            }
        `,
    },
    clearIcon: {
        src: "Cancel",
    },
};

const mapProductsToSlides = (
    products: ProductModel[],
    styles: ProductCompareFlyOutStyles,
    removeProductIdToCompare: (productId: string) => void,
) =>
    products.map(product => ({
        id: product.id,
        renderComponent: function ProductCarouselProductCondensedRenderer() {
            return (
                <ProductCarouselProductCondensed
                    carouselId={PRODUCT_COMPARE_CAROUSEL_ID}
                    product={product}
                    labelsVisible={false}
                    showImage={true}
                    showBrand={false}
                    showTitle={true}
                    showPartNumbers={true}
                    showPrice={true}
                    showAddToCart={false}
                    showAddToList={false}
                    condensedActionsTemplate={(product: ProductModel) => {
                        return (
                            <Button
                                {...styles.removeProductFromCompareButton}
                                onClick={() => removeProductIdToCompare(product.id)}
                                data-test-selector="productCompareRemoveProductButton"
                                data-test-key={product.id}
                            >
                                <VisuallyHidden>
                                    {translate("Remove Product {0} from Compare", product.productTitle)}
                                </VisuallyHidden>
                                <ButtonIcon {...styles.removeProductFromCompareButtonIcon} src="X" />
                            </Button>
                        );
                    }}
                />
            );
        },
    }));

const ProductCompareFlyOut = ({
    extendedStyles,
    history,
    productIds,
    products,
    productComparePageLink,
    currentLocation,
    loadProductIdsToCompare,
    loadProductsToCompare,
    removeProductIdToCompare,
    removeAllProductsFromCompare,
    setProductCompareReturnUrl,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCompareFlyOutStyles, extendedStyles));

    const [slides, setSlides] = useState<CarouselSlide[]>([]);

    useEffect(() => {
        // We load in the ProductIds to Compare from storage
        loadProductIdsToCompare();
    }, []);

    useEffect(() => {
        // Load in products from the ProductCompareService Compare Storage
        if (productIds.length === 0) {
            return;
        }

        loadProductsToCompare({ id: PRODUCT_COMPARE_CAROUSEL_ID, productIds, includeAttributeTypes: true });
    }, [productIds]);

    useEffect(() => {
        if (products.length === 0) {
            return;
        }
        setSlides(mapProductsToSlides(products, styles, productId => removeProductIdToCompare({ productId })));
    }, [products, styles]);

    const handleRemoveAll = () => {
        removeAllProductsFromCompare();
    };
    const handleOpenCompare = () => {
        if (productComparePageLink) {
            setProductCompareReturnUrl({ returnUrl: currentLocation.pathname + currentLocation.search });
            history.push(productComparePageLink.url);
        }
    };

    if (products.length === 0) {
        return null;
    }

    return (
        <Drawer isOpen={true} {...styles.carouselDrawer} data-test-selector="productCompareDrawer">
            <StyledWrapper {...styles.flyOutContent}>
                <ProductCompareHeader styles={styles} productCount={products.length} />
                <StyledWrapper {...styles.carouselDrawerContainer}>
                    {products.map(item => {
                        return (
                            <StyledWrapper key={item.id} {...styles.flyOutContentItem}>
                                <LazyImage
                                    src={item.mediumImagePath}
                                    altText={item.imageAltText}
                                    cloudinary={true}
                                    cloudinaryWidth="85"
                                ></LazyImage>
                                <Clickable
                                    {...styles.removeProductFromCompareButton}
                                    onClick={() => removeProductIdToCompare({ productId: item.id })}
                                >
                                    <Icon color="text.link" size={18} {...styles.clearIcon} />
                                </Clickable>
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
                <ProductCompareActions
                    styles={styles}
                    onOpenCompare={handleOpenCompare}
                    onRemoveAll={handleRemoveAll}
                />
            </StyledWrapper>
        </Drawer>
    );
};

interface ProductCompareHeaderProps {
    styles: ProductCompareFlyOutStyles;
    productCount: number;
}

interface ProductCompareActionsProps {
    styles: ProductCompareFlyOutStyles;
    onOpenCompare: () => void;
    onRemoveAll: () => void;
}

const ProductCompareHeader = ({ styles, productCount }: ProductCompareHeaderProps) => {
    const ItemsText = productCount > 1 ? "Items" : "Item";
    return (
        <Typography {...styles.headerTitleText}>
            {translate(`Comparing {0} ${ItemsText}`, productCount.toString())}
        </Typography>
    );
};

const ProductCompareActions = ({ styles, onOpenCompare, onRemoveAll }: ProductCompareActionsProps) => {
    return (
        <StyledWrapper
            css={css`
                display: flex;
                align-items: center;
            `}
        >
            <Button
                {...styles.headerCompareButton}
                onClick={onOpenCompare}
                data-test-selector="productCompareOpenComparePageButton"
            >
                {translate("Compare")}
            </Button>
            <Clickable
                {...styles.headerRemoveAllButton}
                onClick={onRemoveAll}
                data-test-selector="productCompareRemoveAllButton"
            >
                <Typography {...styles.removeAllText}>{translate("Remove All")}</Typography>
            </Clickable>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductCompareFlyOut));
